import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  MeasuringStrategy,
  PointerSensor,
  closestCorners,
  pointerWithin,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragMoveEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';

import { useAppData } from '@/app-data';
import { useDocumentTitle } from '@/utils/use-document-title';
import * as api from '@/features/backlog/api';
import { Item } from '@/features/backlog/types';
import { priorityRank, hiddenFromBoard } from '@/features/backlog/constants';
import { useStatuses } from '@/features/backlog/statuses';
import { useEpics, focusedEpicForCreate } from '@/features/backlog/epics';
import { useHistory } from '@/features/history/history';
import { existenceCommand, placementCommand, removalCommand, snapshotCommand } from '@/features/history/commands';
import { useLiveItems } from '@/features/realtime/realtime';
import { useFilters } from '@/features/backlog/use-filters';
import { filtersActive, matchesFilters } from '@/features/backlog/filter';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/components/ui/cn';
import { Chevron, Plus } from '@/components/ui/icons';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { ContextMenu, useContextMenu } from '@/components/ui/ContextMenu';
import FilterBar from '@/components/filter-bar';
import CollapseExpand from '@/components/collapse-expand';
import ItemDrawer, { ItemTab } from '@/components/item-drawer';
import { useItemPanel } from '@/components/item-panel';
import { ancestorIds, useItemUrlSync } from '@/features/backlog/use-item-url';
import SavedViews from '@/components/saved-views';
import SortableRow, { EditTarget, GUTTER, GUTTER_PX, INDENT, RowInner } from '@/pages/board/sortable-row';
import ColumnsMenu from '@/pages/board/columns-menu';
import {
  ColKey,
  ColumnState,
  DEFAULT_COLUMN_STATE,
  gridTemplate as buildGridTemplate,
  resolveColumns,
  sanitizeColumnState,
  tableMinWidth,
  visibleKeysOf,
} from '@/pages/board/columns';
import { buildFlat, Flat, getProjection, subtreeIds, visibleSelectedId } from '@/pages/board/tree';
import { duplicateItem } from '@/features/backlog/duplicate';

type Sort = { key: string | null; dir: 'asc' | 'desc' };

// Stable empty collapse set: the filtered tree renders fully expanded (its narrowing comes from the
// visible-id filter, not the collapse state), so it always flattens with nothing folded.
const EMPTY_COLLAPSED = new Set<string>();

// Pointer-based collision: the row under the cursor wins (precise for both real
// dragging and reorder near row edges), falling back to nearest corners.
const collisionDetection: CollisionDetection = (args) => {
  const pointer = pointerWithin(args);
  return pointer.length ? pointer : closestCorners(args);
};

export default function BoardPage() {
  const { currentBoard, members, loading: appLoading, error: appError } = useAppData();
  useDocumentTitle(currentBoard?.name);
  const boardId = currentBoard?.id ?? null;

  const { filters, setFilters } = useFilters();
  const { record, dataVersion } = useHistory();
  const { statuses, defaultValue: defaultStatus, valuesKey } = useStatuses();
  // Sort key → column order, from the board's own status order.
  const statusOrder = useMemo(() => new Map(statuses.map((s, i) => [s.value, i])), [statuses]);
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState<Sort>({ key: null, dir: 'asc' });
  // The item details panel is app-wide (see ItemPanelProvider) so it can stay docked open
  // across a view switch and give a single "selected item" the rows highlight.
  const panel = useItemPanel();
  const { focusedEpicId, epics: epicList } = useEpics();
  // The epic a new item should adopt from the current focus (null when focused on nothing or "Unassigned").
  const focusEpicForNew = focusedEpicForCreate(focusedEpicId);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<EditTarget>(null);

  // ---- configurable table columns (per-member, per-board: order + which are hidden) ----
  const [colState, setColState] = useState<ColumnState>(DEFAULT_COLUMN_STATE);
  // Once the member has changed columns this board-session, a still-in-flight prefs load must not
  // stomp their choice back to the saved value (mirrors the epic-sidebar open-pref guard).
  const colsTouchedRef = useRef(false);
  // Serialise pref writes: each save awaits the previous one, THEN re-reads the freshest full map
  // before writing. Because the server merges only TOP-LEVEL pref keys (replacing the whole
  // `table_columns` object), a naive concurrent read-modify-write would lose updates — chaining +
  // re-reading makes the last write see every prior one (fixes the lost-update on rapid toggles).
  const saveChainRef = useRef<Promise<unknown>>(Promise.resolve());
  useEffect(() => {
    if (!boardId) return undefined;
    let cancelled = false;
    colsTouchedRef.current = false;
    setColState(DEFAULT_COLUMN_STATE); // reset synchronously so a failed load never leaks the prev board's columns
    api
      .getMyPreferences()
      .then((p) => {
        if (cancelled || colsTouchedRef.current) return;
        const saved = p.table_columns?.[boardId];
        setColState(saved === undefined ? DEFAULT_COLUMN_STATE : sanitizeColumnState(saved));
      })
      .catch(() => {
        /* keep the default set on a failed load */
      });
    return () => {
      cancelled = true;
    };
  }, [boardId]);
  const saveColState = useCallback(
    (next: ColumnState) => {
      colsTouchedRef.current = true;
      setColState(next);
      if (!boardId) return;
      saveChainRef.current = saveChainRef.current
        .catch(() => undefined)
        .then(() => api.getMyPreferences())
        .then((p) => api.saveMyPreferences({ table_columns: { ...(p.table_columns ?? {}), [boardId]: next } }))
        .catch(() => {
          /* best-effort; the next successful write carries it */
        });
    },
    [boardId],
  );
  const visibleKeys = useMemo(() => visibleKeysOf(colState), [colState]);
  const visibleColumns = useMemo(() => resolveColumns(visibleKeys), [visibleKeys]);
  const gridTemplate = useMemo(() => buildGridTemplate(visibleColumns), [visibleColumns]);
  const minTableWidth = useMemo(() => tableMinWidth(visibleColumns), [visibleColumns]);
  const onReorderColumns = useCallback((order: ColKey[]) => saveColState({ ...colState, order }), [colState, saveColState]);
  const onToggleColumn = useCallback(
    (key: ColKey) => {
      const isHidden = colState.hidden.includes(key);
      if (!isHidden && visibleKeys.length <= 1) return; // keep at least one column visible
      const hidden = isHidden ? colState.hidden.filter((k) => k !== key) : [...colState.hidden, key];
      saveColState({ ...colState, hidden });
    },
    [colState, visibleKeys.length, saveColState],
  );

  const [confirmDel, setConfirmDel] = useState<Item | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Guards against re-triggering a deep duplicate while its (serial) creates are still in flight.
  const duplicatingRef = useRef(false);

  // drag state
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [offsetX, setOffsetX] = useState(0);
  // True for the single click the browser emits when a row drag ends, so a reorder never also
  // opens the drawer. Reset at the start of every fresh pointer press (capture handler below).
  const dragClick = useRef(false);
  // Right-click / long-press an empty part of the table (or the empty state) to create an item.
  // Rows stop the event first and show their own menu, so this only fires on the table's own space.
  const tableCm = useContextMenu();

  // The FilterBar is sticky (below the app bar); the column header pins directly beneath it. Since
  // the FilterBar's height varies (it wraps on narrow screens), measure it so the column header's
  // sticky `top` — and the scrim's pin line — track it exactly.
  const [fbHeight, setFbHeight] = useState(0);
  const fbObs = useRef<ResizeObserver | null>(null);
  const fbRef = useCallback((el: HTMLDivElement | null) => {
    fbObs.current?.disconnect();
    if (!el) return;
    const measure = () => setFbHeight(el.offsetHeight);
    measure();
    fbObs.current = new ResizeObserver(measure);
    fbObs.current.observe(el);
  }, []);

  // The under-header fade turns on once the column header pins beneath the sticky app bar + FilterBar
  // (56px + fbHeight) and rows scroll under it — at the very top it would sit over the first row's
  // controls. An IntersectionObserver watches the header cross that (moving) pin line: ratio drops
  // below 1 the moment its top clips past it. `headEl` is state so the effect (re)attaches the
  // observer both when the header mounts and whenever fbHeight changes the pin line.
  const [headStuck, setHeadStuck] = useState(false);
  const [headEl, setHeadEl] = useState<HTMLDivElement | null>(null);
  const headRef = useCallback((el: HTMLDivElement | null) => setHeadEl(el), []);
  useEffect(() => {
    if (!headEl) return undefined;
    const obs = new IntersectionObserver(([e]) => setHeadStuck(e.intersectionRatio < 1), {
      threshold: [1],
      rootMargin: `-${57 + fbHeight}px 0px 0px 0px`,
    });
    obs.observe(headEl);
    return () => obs.disconnect();
  }, [headEl, fbHeight]);

  // Whole-row drag (mouse) after 6px of travel, so plain clicks stay reliable.
  // On touch, the row body scrolls; the grip handle (touch-none) starts a drag.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const reload = useCallback(async () => {
    if (!boardId) return;
    setLoading(true);
    try {
      const res = await api.listItems({
        limit: 2000,
        filter: [{ name: 'board_id', op: 'eq', value: boardId }],
        sort: [{ name: 'position', dir: 'asc' }],
      });
      setItems(res.data);
    } finally {
      setLoading(false);
    }
  }, [boardId]);

  // dataVersion bumps after an undo/redo/seek so the view reloads from server truth;
  // valuesKey changes when a status is added/removed (a delete moved items) → refetch.
  useEffect(() => {
    reload();
  }, [reload, dataVersion, valuesKey]);

  // Apply other people's edits live (create/update/move/delete/restore) as they happen.
  useLiveItems(boardId, setItems, reload);

  const memberMap = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);
  const itemsById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);

  // Mirror the open item into a shareable `?item=<key>` URL, and open + center an item a link arrives
  // on. `revealItem` un-collapses a deep-linked item's ancestors so it lands on the item, not its
  // folded parent.
  const revealItem = useCallback(
    (id: string) => {
      const anc = ancestorIds(id, itemsById);
      if (anc.size === 0) return;
      setCollapsed((prev) => {
        if (![...anc].some((a) => prev.has(a))) return prev;
        const next = new Set(prev);
        anc.forEach((a) => next.delete(a));
        return next;
      });
    },
    [itemsById],
  );
  useItemUrlSync(items, { revealItem });
  // Rows shown in the table: epics/initiatives are hidden (they have their own UI — epics live in
  // the left rail), matching the Kanban. Any children they had render promoted to top level (their
  // now-absent parent makes buildFlat treat them as roots). Mutations still use the full `items`.
  const boardItems = useMemo(() => items.filter((i) => !hiddenFromBoard(i)), [items]);
  const childCounts = useMemo(() => {
    const c = new Map<string, number>();
    boardItems.forEach((i) => i.parent_id && c.set(i.parent_id, (c.get(i.parent_id) ?? 0) + 1));
    return c;
  }, [boardItems]);
  const parentsWithChildren = useMemo(() => new Set(childCounts.keys()), [childCounts]);

  const filterActive = filtersActive(filters);
  // Three view modes:
  //   treeMode       — unfiltered + unsorted: the nested tree with drag-to-reorder / nest.
  //   sortMode       — a sort is active: a FLAT list (nesting dropped) ordered by the sort key.
  //   filterTreeMode — filtered (but unsorted): keep the NESTED layout, narrowed to matching
  //                    branches like the Kanban — a parent with no matching descendant is hidden,
  //                    matches read at full strength with their ancestors/siblings dimmed for context.
  const sortMode = !!sort.key;
  const treeMode = !filterActive && !sortMode;
  const filterTreeMode = filterActive && !sortMode;
  // Resolve an item's epic_id to the epic item (for the coloured chip above the key + title).
  // Prefer the live EpicsProvider list (which reflects colour/rank edits made in the sidebar even
  // within THIS tab — the board's own useLiveItems skips this tab's echoes), falling back to the
  // board's items. Without this, recolouring an epic wouldn't re-tint the table chips live.
  const epicById = useMemo(() => new Map(epicList.map((e) => [e.id, e])), [epicList]);
  const epicOf = useCallback(
    (id: string | null) => (id ? epicById.get(id) ?? itemsById.get(id) : undefined),
    [epicById, itemsById],
  );

  // The row to ring for the open item: itself when visible, else the collapsed ancestor it's
  // folded inside. Flat (filtered or sorted) mode has no nesting, so it's a straight id there.
  const highlightId = useMemo(() => {
    if (!panel.selectedId) return null;
    if (!treeMode) return panel.selectedId;
    return visibleSelectedId(panel.selectedId, itemsById, (cid) => collapsed.has(cid));
  }, [panel.selectedId, treeMode, collapsed, itemsById]);

  const flatFull = useMemo(() => buildFlat(boardItems, collapsed), [boardItems, collapsed]);
  const activeSub = useMemo(() => (activeId ? subtreeIds(items, activeId) : null), [items, activeId]);
  // Rows used for reorder math: while dragging, the active item's descendants are
  // treated as part of its subtree (one unit) and excluded here — but they STAY
  // RENDERED (see `rows` below), just inert, so the node never visibly collapses.
  const projRows: Flat[] = useMemo(
    () => (activeSub ? flatFull.filter((f) => f.item.id === activeId || !activeSub.has(f.item.id)) : flatFull),
    [flatFull, activeSub, activeId],
  );

  const flatFilteredSorted = useMemo(() => {
    let out = boardItems.filter((it) => matchesFilters(it, filters, memberMap));
    if (sort.key) {
      const k = sort.key;
      const val = (it: Item): string | number => {
        switch (k) {
          case 'title':
            return it.title.toLowerCase();
          case 'status':
            return statusOrder.get(it.status) ?? statuses.length;
          case 'priority':
            return priorityRank(it.priority);
          case 'assignee':
            return (memberMap.get(it.assignee_id ?? '')?.name ?? '').toLowerCase();
          case 'start_date':
            return it.start_date ?? '';
          case 'end_date':
            return it.end_date ?? '';
          default:
            return it.position;
        }
      };
      out = [...out].sort((a, b) => {
        const va = val(a);
        const vb = val(b);
        const cmp = va < vb ? -1 : va > vb ? 1 : 0;
        return sort.dir === 'asc' ? cmp : -cmp;
      });
    }
    return out;
  }, [boardItems, filters, sort, memberMap, statusOrder, statuses.length]);

  // ---- filtered-tree narrowing (mirrors the Kanban's in-place, context-preserving filter) ----
  const matchIds = useMemo(() => {
    if (!filterTreeMode) return null;
    const s = new Set<string>();
    for (const it of boardItems) if (matchesFilters(it, filters, memberMap)) s.add(it.id);
    return s;
  }, [filterTreeMode, boardItems, filters, memberMap]);
  // A match plus every ANCESTOR of a match — the branches worth keeping. A parent with no matching
  // descendant is dropped entirely (bounded + cycle-safe, mirroring the Kanban).
  const branchIds = useMemo(() => {
    if (!matchIds) return null;
    const s = new Set(matchIds);
    for (const id of matchIds) {
      let it = itemsById.get(id);
      for (let i = 0; i < 10000 && it?.parent_id && itemsById.has(it.parent_id); i++) {
        it = itemsById.get(it.parent_id)!;
        s.add(it.id);
      }
    }
    return s;
  }, [matchIds, itemsById]);
  // What actually renders: every branch row PLUS the direct siblings of a match (the other children
  // of a match's present parent), kept as dimmed context — the same rule the Kanban uses.
  const filterVisibleIds = useMemo(() => {
    if (!branchIds || !matchIds) return null;
    const s = new Set(branchIds);
    const matchParents = new Set<string>();
    for (const id of matchIds) {
      const p = itemsById.get(id)?.parent_id;
      if (p && itemsById.has(p)) matchParents.add(p);
    }
    for (const it of boardItems) if (it.parent_id && matchParents.has(it.parent_id)) s.add(it.id);
    return s;
  }, [branchIds, matchIds, boardItems, itemsById]);
  // The narrowed tree, fully expanded (its narrowing is the visible-id filter, not the collapse
  // state, so every match shows regardless of what the user had folded) then pruned to that set.
  const filterFlat = useMemo(() => {
    if (!filterTreeMode || !filterVisibleIds) return [] as Flat[];
    return buildFlat(boardItems, EMPTY_COLLAPSED).filter((f) => filterVisibleIds.has(f.item.id));
  }, [filterTreeMode, boardItems, filterVisibleIds]);

  // Rendered rows: the FULL tree (descendants included) so a dragged parent's children stay visible;
  // the narrowed tree while filtering; else the flat sorted list.
  const rows: Flat[] = treeMode
    ? flatFull
    : filterTreeMode
      ? filterFlat
      : flatFilteredSorted.map((it) => ({ item: it, depth: 0 }));
  // Sortable ids exclude the dragged subtree's descendants (they render, but inert).
  const sortableIds = (treeMode ? projRows : rows).map((r) => r.item.id);

  const projection =
    treeMode && activeId && overId ? getProjection(projRows, activeId, overId, offsetX, INDENT) : null;

  // ---- inline edit ----
  const saveField = async (item: Item, patch: Partial<Item>) => {
    const merged = { ...item, ...patch };
    setItems((cur) => cur.map((i) => (i.id === item.id ? merged : i)));
    try {
      const saved = await api.updateItem(item.id, {
        board_id: merged.board_id,
        parent_id: merged.parent_id,
        epic_id: merged.epic_id,
        title: merged.title,
        description: merged.description,
        status: merged.status,
        priority: merged.priority,
        type: merged.type,
        assignee_id: merged.assignee_id,
        pdm_id: merged.pdm_id,
        source: merged.source,
        start_date: merged.start_date,
        end_date: merged.end_date,
      });
      setItems((cur) => cur.map((i) => (i.id === item.id ? saved : i)));
      record(snapshotCommand(item, saved, `Edit ${item.key}`));
    } catch {
      reload();
    }
  };

  // Link an item to an epic dropped from the sidebar, cascading to its whole subtree so every
  // nested subtask picks up the same epic. Optimistically links the subtree here (computed from the
  // freshest state inside the updater, so the callback needn't depend on `items`); the server does
  // the same cascade atomically. Narrow write — only epic_id changes across the subtree.
  const linkEpic = useCallback(
    (itemId: string, epicId: string) => {
      if (itemId === epicId) return;
      setItems((cur) => {
        const ids = subtreeIds(cur, itemId);
        // Mirror the server cascade: (re)link the drop target itself, and cascade only to subtasks
        // that have NO epic yet — a subtask already on another epic is a deliberate choice we leave
        // alone — and never to a nested epic.
        return cur.map((i) =>
          ids.has(i.id) && i.id !== epicId && (i.id === itemId || (i.type !== 'epic' && !i.epic_id))
            ? { ...i, epic_id: epicId }
            : i,
        );
      });
      api.setItemEpic(itemId, epicId, { cascade: true }).catch(() => reload());
    },
    [reload],
  );

  // Insert a new issue as a sibling immediately above/below `item` (same nesting level),
  // then drop straight into inline title editing on the new row.
  const addSibling = async (item: Item, where: 'above' | 'below') => {
    const parentKey = item.parent_id ?? null;
    const sibs = items
      .filter((i) => (i.parent_id ?? null) === parentKey)
      .sort((a, b) => a.position - b.position);
    const idx = sibs.findIndex((s) => s.id === item.id);
    let position: number;
    if (where === 'above') {
      const prev = sibs[idx - 1];
      position = prev ? (prev.position + item.position) / 2 : item.position - 1;
    } else {
      const next = sibs[idx + 1];
      position = next ? (item.position + next.position) / 2 : item.position + 1;
    }
    // Epic: a nested sibling inherits its parent's epic; a top-level row adopts the focused epic.
    const parent = parentKey ? items.find((i) => i.id === parentKey) : null;
    const epicId = parent ? parent.epic_id ?? focusEpicForNew : focusEpicForNew;
    try {
      const created = await api.createItem({
        board_id: item.board_id,
        parent_id: parentKey,
        title: 'New item',
        status: defaultStatus,
        position,
        ...(epicId ? { epic_id: epicId } : {}),
      });
      setItems((cur) => [...cur, created]);
      setEditing({ id: created.id, field: 'title' });
      record(existenceCommand(created.id, [created.id], created.board_id, `New ${created.key}`));
    } catch {
      reload();
    }
  };

  // Add a nested child under `parentId`, expanding the parent so the new row shows, then edit it.
  const addSub = async (parentId: string) => {
    const parent = items.find((i) => i.id === parentId);
    if (!parent) return;
    // In the flat (filtered or sorted) view a fresh "New item" wouldn't match the active filter
    // and would render nowhere, stranding the inline editor — fall back to the drawer's create
    // form there. (Only tree mode keeps the whole tree, so the new child does show.)
    // A sub-item inherits its parent's epic (falling back to the focused epic when the parent has none).
    const subEpicId = parent.epic_id ?? focusEpicForNew;
    if (!treeMode) {
      panel.openCreate({ initialParentId: parentId, initialEpicId: subEpicId });
      return;
    }
    const kids = items.filter((i) => i.parent_id === parentId).sort((a, b) => a.position - b.position);
    const last = kids[kids.length - 1];
    const position = last ? last.position + 1 : 1000;
    setCollapsed((s) => {
      const n = new Set(s);
      n.delete(parentId);
      return n;
    });
    try {
      const created = await api.createItem({
        board_id: parent.board_id,
        parent_id: parentId,
        title: 'New item',
        status: defaultStatus,
        position,
        ...(subEpicId ? { epic_id: subEpicId } : {}),
      });
      setItems((cur) => [...cur, created]);
      setEditing({ id: created.id, field: 'title' });
      record(existenceCommand(created.id, [created.id], created.board_id, `New ${created.key}`));
    } catch {
      reload();
    }
  };

  // Deep-duplicate an issue (with its subtree), dropped in right below the original. Guards
  // re-entry (the copy runs a serial batch of creates) and shows the "Saving…" cue meanwhile.
  const duplicate = async (item: Item) => {
    if (duplicatingRef.current) return;
    duplicatingRef.current = true;
    setLoading(true);
    const parentKey = item.parent_id ?? null;
    const sibs = items.filter((i) => (i.parent_id ?? null) === parentKey).sort((a, b) => a.position - b.position);
    const idx = sibs.findIndex((s) => s.id === item.id);
    const next = sibs[idx + 1];
    const position = next ? (item.position + next.position) / 2 : item.position + 1;
    try {
      const created = await duplicateItem(items, item.id, position);
      setItems((cur) => [...cur, ...created]);
      if (created.length) {
        record(
          existenceCommand(created[0].id, created.map((c) => c.id), created[0].board_id, `Duplicate ${item.key}`),
        );
      }
    } catch {
      // A create failed partway through the subtree; reload() below reconciles to server truth.
    } finally {
      duplicatingRef.current = false;
      reload();
    }
  };

  // "Bring to top" / "Send to bottom" from the row menu — move the item to the far end of its
  // SIBLING group (same parent_id), respecting nesting (mirrors the kanban card menu). A pure
  // position change via reparentItem (parent unchanged), with placementCommand for undo.
  const reorderExtreme = async (item: Item, edge: 'top' | 'bottom') => {
    const parentKey = item.parent_id ?? null;
    const sibs = items
      .filter((i) => (i.parent_id ?? null) === parentKey && i.id !== item.id)
      .sort((a, b) => a.position - b.position);
    if (sibs.length === 0) return; // only child — nothing to reorder against
    const end = edge === 'top' ? sibs[0] : sibs[sibs.length - 1];
    const position = edge === 'top' ? end.position - 1 : end.position + 1;
    if (item.position === position) return;
    setItems((c) => c.map((i) => (i.id === item.id ? { ...i, position } : i)));
    try {
      await api.reparentItem(item.id, parentKey, position);
      record(
        placementCommand(
          item.id,
          { parent_id: item.parent_id, status: item.status, position: item.position },
          { parent_id: parentKey, status: item.status, position },
          item.board_id,
          `${edge === 'top' ? 'Bring to top' : 'Send to bottom'} ${item.key}`,
        ),
      );
    } catch {
      reload();
    }
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    const target = confirmDel;
    const ids = subtreeIds(items, target.id);
    setDeleting(true);
    try {
      const res = await api.deleteItem(target.id);
      setItems((cur) => cur.filter((i) => !ids.has(i.id)));
      setConfirmDel(null);
      record(removalCommand(target.id, res.ids ?? [...ids], target.board_id, `Delete ${target.key}`));
    } catch {
      reload();
    } finally {
      setDeleting(false);
    }
  };

  const delCount = confirmDel ? subtreeIds(items, confirmDel.id).size - 1 : 0;

  const toggle = (id: string) =>
    setCollapsed((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  // ---- drag ----
  const onDragStart = (e: DragStartEvent) => {
    setActiveId(String(e.active.id));
    setOverId(String(e.active.id));
    setEditing(null);
    dragClick.current = true; // a drag began — swallow the click its release will emit
  };
  const onDragMove = (e: DragMoveEvent) => setOffsetX(e.delta.x);
  const onDragOver = (e: DragOverEvent) => setOverId(e.over ? String(e.over.id) : null);
  const onDragCancel = () => {
    setActiveId(null);
    setOverId(null);
    setOffsetX(0);
  };
  const onDragEnd = async (e: DragEndEvent) => {
    const aId = String(e.active.id);
    const oId = e.over ? String(e.over.id) : null;
    const proj = oId ? getProjection(projRows, aId, oId, offsetX, INDENT) : null;
    setActiveId(null);
    setOverId(null);
    setOffsetX(0);
    if (!oId || !proj) return;

    const activeIndex = projRows.findIndex((f) => f.item.id === aId);
    const overIndex = projRows.findIndex((f) => f.item.id === oId);
    if (activeIndex === -1 || overIndex === -1) return;
    const moved = arrayMove(projRows, activeIndex, overIndex);
    const parentKey = proj.parentId;
    const siblings = moved.filter((f) => (f.item.id === aId ? parentKey : f.item.parent_id ?? null) === parentKey);
    const idx = siblings.findIndex((f) => f.item.id === aId);
    const before = siblings[idx - 1]?.item;
    const after = siblings[idx + 1]?.item;
    let position: number;
    if (!before && !after) position = 1000;
    else if (!before) position = after.position - 1;
    else if (!after) position = before.position + 1;
    else position = (before.position + after.position) / 2;

    const cur = items.find((i) => i.id === aId);
    if (cur && cur.parent_id === (parentKey ?? null) && cur.position === position) return;
    setItems((c) => c.map((i) => (i.id === aId ? { ...i, parent_id: parentKey, position } : i)));
    try {
      await api.reparentItem(aId, parentKey, position);
      if (cur) {
        record(
          placementCommand(
            aId,
            { parent_id: cur.parent_id, status: cur.status, position: cur.position },
            { parent_id: parentKey, status: cur.status, position },
            cur.board_id,
            `Move ${cur.key}`,
          ),
        );
      }
    } finally {
      reload();
    }
  };

  const activeItem = activeId ? items.find((i) => i.id === activeId) ?? null : null;

  if (appLoading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }
  if (appError) return <div className="py-20 text-center text-danger">{appError}</div>;
  if (!currentBoard) return <div className="py-20 text-center text-fg-muted">No board selected.</div>;

  return (
    <div>
      {/* Sticky toolbar: the search + filters + actions stay pinned below the app bar as the list
          scrolls (the column header then pins directly beneath this). Frosted (translucent + blur,
          matching the app header) rather than a solid fill. z-30 keeps its facet dropdowns above the
          column header (z-25). */}
      <div ref={fbRef} className="sticky top-14 z-30 bg-ink/80 backdrop-blur">
      <FilterBar
        filters={filters}
        onChange={setFilters}
        items={items}
        members={members}
        extraActive={!!sort.key}
        onReset={() => setSort({ key: null, dir: 'asc' })}
        trailing={
          <>
            {loading ? <span className="text-[12px] text-fg-faint">Saving…</span> : null}
            <CollapseExpand
              show={treeMode && parentsWithChildren.size > 0}
              onCollapseAll={() => setCollapsed(new Set(parentsWithChildren))}
              onExpandAll={() => setCollapsed(new Set())}
            />
            <ColumnsMenu
              order={colState.order}
              hidden={colState.hidden}
              onReorder={onReorderColumns}
              onToggle={onToggleColumn}
            />
            <SavedViews boardId={currentBoard.id} filters={filters} onApply={setFilters} />
          </>
        }
      />
      </div>

      {/* Header — no inner scroll; the page body handles overflow (the table sets its own min width
          from the visible columns, so showing more columns scrolls the page horizontally). */}
      <div>
        <div className="relative" style={{ minWidth: minTableWidth }}>
          {/* Table frame — side + bottom border with rounded bottom corners wrap ONLY the columns, so
              the # rank and the hover drag/details buttons sit in the page margin, outside the table.
              No TOP border: the column header defines the top edge, so the sticky frosted filter bar
              never clashes with a top hairline. */}
          <div
            className="pointer-events-none absolute inset-y-0 right-0 rounded-b-card border-x border-b border-line"
            style={{ left: GUTTER_PX }}
            aria-hidden="true"
          />
          <div
            className="relative"
            {...tableCm.triggerProps}
            // A fresh press clears the guard; the click a just-ended row drag emits arrives while
            // the guard is set and is swallowed here (capture phase) before a row's onClick opens it.
            onPointerDownCapture={() => {
              dragClick.current = false;
            }}
            onClickCapture={(e) => {
              if (dragClick.current) {
                e.stopPropagation();
                dragClick.current = false;
              }
            }}
          >
          {/* Column headers stay pinned below the app bar + sticky FilterBar (56px + fbHeight) while
              the rows scroll. */}
          <div
            ref={headRef}
            className={cn('table-head-sticky sticky z-[25] flex items-stretch', headStuck && 'is-stuck')}
            style={{ top: `calc(3.5rem + ${fbHeight}px)` }}
          >
            <div className={cn(GUTTER, 'flex shrink-0 items-center justify-end bg-ink px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-fg-faint')}>
              {/* Sort by the board's own priority order (position) — the default nested order, flat. */}
              <SortHeader label="#" k="position" sort={sort} setSort={setSort} />
            </div>
            {/* Side hairlines via inset shadow (not border-x, which would shift the columns
                out of line with the rows): they sit exactly on the frame's side borders the
                pinned header would otherwise cover. Columns are dynamic (see the Columns menu). */}
            <div
              className="grid flex-1 items-center border-b border-line bg-surface-inset/90 px-1 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-fg-faint shadow-[inset_1px_0_0_var(--line),inset_-1px_0_0_var(--line)] backdrop-blur"
              style={{ gridTemplateColumns: gridTemplate }}
            >
              <div className="pl-1">
                <SortHeader label="Title" k="title" sort={sort} setSort={setSort} />
              </div>
              {visibleColumns.map((col) => (
                <div key={col.key} className={cn('px-1', col.align === 'center' && 'text-center')}>
                  {col.sortKey ? (
                    <SortHeader label={col.label} k={col.sortKey} sort={sort} setSort={setSort} />
                  ) : (
                    col.label
                  )}
                </div>
              ))}
            </div>
          </div>

          {rows.length === 0 ? (
            <div className="px-4 py-10 text-center text-fg-faint">
              {loading ? 'Loading…' : filterActive ? 'No items match your filters.' : 'No items yet.'}
            </div>
          ) : !treeMode ? (
            // Filtered (narrowed nested tree, matches lifted / context dimmed) or sorted (flat list).
            // Static — no drag — but inline edit + expand/collapse-free hierarchy still read. Depth
            // is 0 in sort mode (flat) and the real tree depth in filter mode.
            <div>
              {rows.map((f, i) => (
                <RowInner
                  key={f.item.id}
                  item={f.item}
                  rank={i + 1}
                  depth={filterTreeMode ? f.depth : 0}
                  hasChildren={false}
                  collapsed={false}
                  dimmed={filterTreeMode && !!matchIds && !matchIds.has(f.item.id)}
                  columns={visibleColumns}
                  gridTemplate={gridTemplate}
                  epicOf={epicOf}
                  highlightId={highlightId}
                  members={members}
                  memberMap={memberMap}
                  editing={editing}
                  onEdit={setEditing}
                  onSave={saveField}
                  onToggle={toggle}
                  onOpenDrawer={(id, tab) => panel.openItem(id, tab)}
                  onAddSub={addSub}
                  onAddSibling={addSibling}
                  onDuplicate={duplicate}
                  onDelete={(it) => setConfirmDel(it)}
                  onReorderExtreme={reorderExtreme}
                  onLinkEpic={linkEpic}
                  focusedEpicId={focusedEpicId}
                />
              ))}
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={collisionDetection}
              measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
              onDragStart={onDragStart}
              onDragMove={onDragMove}
              onDragOver={onDragOver}
              onDragEnd={onDragEnd}
              onDragCancel={onDragCancel}
            >
              <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                {rows.map((f, i) => (
                  <SortableRow
                    key={f.item.id}
                    item={f.item}
                    rank={i + 1}
                    depth={treeMode ? f.depth : 0}
                    hasChildren={treeMode && (childCounts.get(f.item.id) ?? 0) > 0}
                    collapsed={collapsed.has(f.item.id)}
                    disabled={!!activeSub && activeSub.has(f.item.id) && f.item.id !== activeId}
                    columns={visibleColumns}
                    gridTemplate={gridTemplate}
                    epicOf={epicOf}
                    highlightId={highlightId}
                    members={members}
                    memberMap={memberMap}
                    editing={editing}
                    onEdit={setEditing}
                    onSave={saveField}
                    onToggle={toggle}
                    onOpenDrawer={(id, tab) => panel.openItem(id, tab)}
                    onAddSub={addSub}
                    onAddSibling={addSibling}
                    onDuplicate={duplicate}
                    onDelete={(it) => setConfirmDel(it)}
                    onLinkEpic={linkEpic}
                    focusedEpicId={focusedEpicId}
                  />
                ))}
              </SortableContext>
              <DragOverlay>
                {activeItem ? (
                  <div className="rounded-card border border-accent/40 bg-surface-raised shadow-2xl shadow-black/60">
                    <RowInner
                      item={activeItem}
                      rank={0}
                      depth={projection ? projection.depth : 0}
                      hasChildren={(childCounts.get(activeItem.id) ?? 0) > 0}
                      collapsed={collapsed.has(activeItem.id)}
                      columns={visibleColumns}
                      gridTemplate={gridTemplate}
                      epicOf={epicOf}
                      members={members}
                      memberMap={memberMap}
                      editing={null}
                      onEdit={() => {}}
                      onSave={() => {}}
                      onToggle={() => {}}
                      onOpenDrawer={() => {}}
                      onAddSub={() => {}}
                      onAddSibling={() => {}}
                      onDuplicate={() => {}}
                      onDelete={() => {}}
                      draggable
                      overlay
                    />
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          )}
          </div>
        </div>
      </div>

      <ItemDrawer
        open={panel.open}
        itemId={panel.itemId}
        boardId={currentBoard.id}
        initialParentId={panel.initialParentId ?? null}
        initialType={panel.initialType}
        initialEpicId={panel.initialEpicId ?? null}
        initialTab={panel.initialTab as ItemTab | undefined}
        items={items}
        onClose={panel.close}
        onSaved={reload}
      />

      <ConfirmDialog
        open={!!confirmDel}
        onClose={() => setConfirmDel(null)}
        onConfirm={doDelete}
        title="Delete item?"
        message={
          <>
            Delete <b className="text-fg-strong">{confirmDel?.title}</b>
            {delCount > 0 ? ` and its ${delCount} sub-item${delCount > 1 ? 's' : ''}` : ''}? This can’t be undone.
          </>
        }
        confirmLabel="Delete"
        busy={deleting}
      />

      {tableCm.open ? (
        <ContextMenu
          open
          x={tableCm.x}
          y={tableCm.y}
          onClose={tableCm.close}
          items={[
            {
              label: 'New item',
              icon: <Plus />,
              onSelect: () => panel.openCreate({ initialEpicId: focusEpicForNew }),
            },
          ]}
        />
      ) : null}
    </div>
  );
}

function SortHeader({
  label,
  k,
  sort,
  setSort,
}: {
  label: string;
  k: string;
  sort: Sort;
  setSort: React.Dispatch<React.SetStateAction<Sort>>;
}) {
  const active = sort.key === k;
  return (
    <button
      type="button"
      onClick={() =>
        setSort((s) => (s.key === k ? { key: k, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: k, dir: 'asc' }))
      }
      className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-fg-soft"
    >
      {label}
      <Chevron
        className={cn(
          'h-3 w-3 rotate-90 transition-transform',
          active ? (sort.dir === 'asc' ? 'rotate-[270deg] text-accent' : 'rotate-90 text-accent') : 'opacity-30',
        )}
      />
    </button>
  );
}
